import { BarChart3, CalendarCheck, FileText, PieChart, Clock, CircleDollarSign, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface LayoutItem {
  id: string;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  position?: {
    x: number;
    y: number;
  };
}

export interface DashboardLayout {
  items: LayoutItem[];
  type?: 'grid' | 'freeform';
}

export const USER_DASHBOARD_LAYOUT_KEY = 'user-dashboard-layout';
export const ADMIN_DASHBOARD_LAYOUT_KEY = 'admin-dashboard-layout';

export const sizeToGridClass = {
  'small': 'col-span-1', // 1/4 width
  'medium': 'col-span-2', // 1/2 width
  'large': 'col-span-4', // full width
  'xlarge': 'col-span-4', // full width, taller
};

export const DEFAULT_USER_WIDGET_ORDER: LayoutItem[] = [
  { id: 'total-contracts', size: 'small', position: { x: 0, y: 0 } },
  { id: 'active-contracts', size: 'small', position: { x: 1, y: 0 } },
  { id: 'expiring-contracts', size: 'small', position: { x: 2, y: 0 } },
  { id: 'user-total-value', size: 'small', position: { x: 3, y: 0 } },
  { id: 'contracts-timeline', size: 'large', position: { x: 0, y: 1 } },
  { id: 'recent-contracts', size: 'medium', position: { x: 0, y: 2 } },
  { id: 'upcoming-expirations', size: 'medium', position: { x: 2, y: 2 } },
  { id: 'contracts-distribution', size: 'medium', position: { x: 0, y: 3 } },
  { id: 'contract-costs-by-type', size: 'medium', position: { x: 2, y: 3 } },
  { id: 'financial-result', size: 'small', position: { x: 0, y: 4 } },
];

export const WIDGET_DEFINITIONS = [
  {
    id: 'total-contracts',
    name: 'Total Contracts',
    description: 'Shows the total number of contracts',
    icon: FileText,
    defaultSize: 'small'
  },
  {
    id: 'active-contracts',
    name: 'Active Contracts',
    description: 'Shows the number of currently active contracts',
    icon: FileText,
    defaultSize: 'small'
  },
  {
    id: 'expiring-contracts',
    name: 'Expiring Contracts',
    description: 'Shows contracts expiring in the next 30 days',
    icon: Clock,
    defaultSize: 'small'
  },
  {
    id: 'user-total-value',
    name: 'Total Value',
    description: 'Total value of all contracts in your default currency',
    icon: CircleDollarSign,
    defaultSize: 'small'
  },
  {
    id: 'contracts-timeline',
    name: 'Contracts Timeline',
    description: 'Chart showing contract creation over time',
    icon: BarChart3,
    defaultSize: 'large'
  },
  {
    id: 'contracts-distribution',
    name: 'Contracts Distribution',
    description: 'Chart showing distribution of contracts by type',
    icon: PieChart,
    defaultSize: 'medium'
  },
  {
    id: 'recent-contracts',
    name: 'Recent Contracts',
    description: 'List of recently created contracts',
    icon: FileText,
    defaultSize: 'medium'
  },
  {
    id: 'upcoming-expirations',
    name: 'Upcoming Expirations',
    description: 'List of contracts that are expiring soon',
    icon: CalendarCheck,
    defaultSize: 'medium'
  },
  {
    id: 'contract-costs-by-type',
    name: 'Monthly Costs by Type',
    description: 'Monthly contract costs grouped by type',
    icon: PieChart,
    defaultSize: 'medium'
  },
  {
    id: 'financial-result',
    name: 'Financial Result',
    description: 'Shows total income minus costs and financial summary',
    icon: TrendingUp,
    defaultSize: 'small'
  }
];

export const fetchSavedLayout = async (dashboardType = 'admin-dashboard') => {
  try {
    // Try to get from local storage first
    const localLayoutKey = dashboardType === 'admin-dashboard' ? 
      ADMIN_DASHBOARD_LAYOUT_KEY : USER_DASHBOARD_LAYOUT_KEY;
    
    const localLayout = localStorage.getItem(localLayoutKey);
    
    if (localLayout) {
      console.log(`Found local ${dashboardType} layout in localStorage`);
      return JSON.parse(localLayout);
    }
    
    // If not in localStorage, check database
    const { data: dashboardLayout, error } = await getDashboardLayoutFromDatabase(dashboardType);
    
    if (error) {
      console.error(`Error fetching ${dashboardType} layout from database:`, error);
      throw error;
    }
    
    if (dashboardLayout) {
      // Cache in localStorage
      localStorage.setItem(localLayoutKey, JSON.stringify(dashboardLayout));
      return dashboardLayout;
    }
    
    // Return null if no layout found
    return null;
  } catch (error) {
    console.error(`Error in fetchSavedLayout for ${dashboardType}:`, error);
    throw error;
  }
};

export const saveLayoutToDatabase = async (
  layout: DashboardLayout, 
  dashboardType = 'admin-dashboard',
  toast: any, 
  setIsSaving: (value: boolean) => void, 
  setSavedToDb: (value: boolean) => void
) => {
  try {
    setIsSaving(true);
    
    // Save to localStorage first as a backup
    const localLayoutKey = dashboardType === 'admin-dashboard' ? 
      ADMIN_DASHBOARD_LAYOUT_KEY : USER_DASHBOARD_LAYOUT_KEY;
    
    localStorage.setItem(localLayoutKey, JSON.stringify(layout));
    
    // Call the Supabase function to save to database
    const { error } = await saveDashboardLayoutToDatabase(layout, dashboardType);
    
    if (error) {
      toast({
        title: "Error Saving Layout",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
    
    toast({
      title: "Layout Saved",
      description: "Your dashboard layout has been saved to your account.",
    });
    
    setSavedToDb(true);
    
    // Reset saved indicator after a delay
    setTimeout(() => {
      setSavedToDb(false);
    }, 3000);
  } catch (error) {
    console.error(`Error saving ${dashboardType} layout to database:`, error);
    
    toast({
      title: "Error Saving Layout",
      description: "There was a problem saving your dashboard layout.",
      variant: "destructive",
    });
  } finally {
    setIsSaving(false);
  }
};

async function getDashboardLayoutFromDatabase(dashboardType = 'admin-dashboard') {
  try {
    const { data, error } = await supabase.rpc('get_dashboard_layout', {
      dashboard_type: dashboardType
    });
    
    return { data, error };
  } catch (error) {
    console.error("Error in getDashboardLayoutFromDatabase:", error);
    return { data: null, error };
  }
}

async function saveDashboardLayoutToDatabase(layout: DashboardLayout, dashboardType = 'admin-dashboard') {
  try {
    // Create a serializable plain object from our layout that will work with Supabase's JSON type
    const jsonCompatibleLayout = {
      items: layout.items.map(item => ({
        id: item.id,
        size: item.size,
        position: item.position
      }))
    };
    
    const { data, error } = await supabase.rpc('update_dashboard_layout', {
      new_layout: jsonCompatibleLayout,
      in_dashboard_type: dashboardType
    });
    
    if (error) {
      console.error("Error in saveDashboardLayoutToDatabase:", error);
    }
    
    return { error };
  } catch (error) {
    console.error("Error in saveDashboardLayoutToDatabase:", error);
    return { error };
  }
}
